/* lunal — theme.js */
(() => {
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const L = Object.assign(
    { routes: { cart: '/cart', cart_add: '/cart/add', cart_change: '/cart/change' }, moneyFormat: '{{amount_with_comma_separator}} €' },
    window.lunal || {}
  );
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Money ---------- */
  const money = (cents) => {
    const n = (Number(cents) || 0) / 100;
    const fmt = (d, th, dec) => {
      const [a, b] = n.toFixed(d).split('.');
      return a.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (b ? dec + b : '');
    };
    const map = {
      amount: () => fmt(2, ',', '.'),
      amount_no_decimals: () => fmt(0, ',', '.'),
      amount_with_comma_separator: () => fmt(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => fmt(0, '.', ','),
      amount_with_space_separator: () => fmt(2, ' ', ','),
      amount_with_apostrophe_separator: () => fmt(2, "'", '.'),
    };
    return L.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => (map[k] || map.amount)());
  };

  /* ---------- Slideshow ---------- */
  const initSlideshow = (root) => {
    const slides = $$('[data-slide]', root);
    const dots = $$('[data-dot]', root);
    if (slides.length < 2) return;
    let index = 0;
    let timer;
    const delay = Number(root.dataset.autoplay) || 0;

    const syncTheme = () => {
      root.dataset.theme = slides[index].classList.contains('hero__slide--dark') ? 'dark' : 'light';
    };
    const go = (n) => {
      slides[index].classList.remove('is-active');
      slides[index].setAttribute('aria-hidden', 'true');
      dots[index]?.classList.remove('is-active');
      dots[index]?.removeAttribute('aria-current');
      index = (n + slides.length) % slides.length;
      slides[index].classList.add('is-active');
      slides[index].removeAttribute('aria-hidden');
      dots[index]?.classList.add('is-active');
      dots[index]?.setAttribute('aria-current', 'true');
      syncTheme();
    };
    const play = () => {
      clearInterval(timer);
      if (delay && !reduceMotion) timer = setInterval(() => go(index + 1), delay);
    };
    const stop = () => clearInterval(timer);

    $('[data-prev]', root)?.addEventListener('click', () => { go(index - 1); play(); });
    $('[data-next]', root)?.addEventListener('click', () => { go(index + 1); play(); });
    dots.forEach((dot, i) => dot.addEventListener('click', () => { go(i); play(); }));
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', play);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', play);

    // Swipe
    let startX = null;
    root.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
    root.addEventListener('touchend', (e) => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 50) { go(index + (dx < 0 ? 1 : -1)); play(); }
      startX = null;
    });

    // Theme editor: jump to the selected slide
    document.addEventListener('shopify:block:select', (e) => {
      const i = slides.indexOf(e.target);
      if (i > -1) { go(i); stop(); }
    });

    syncTheme();
    play();
  };

  /* ---------- Tabs ---------- */
  const initTabs = (root) => {
    const tabs = $$('[data-tab]', root);
    const panels = $$('[data-panel]', root);
    const activate = (idx) => {
      tabs.forEach((t, j) => {
        t.classList.toggle('is-active', j === idx);
        t.setAttribute('aria-selected', String(j === idx));
        t.tabIndex = j === idx ? 0 : -1;
      });
      panels.forEach((p, j) => { p.hidden = j !== idx; });
      revealIn(panels[idx]);
    };
    tabs.forEach((tab, idx) => {
      tab.addEventListener('click', () => activate(idx));
      tab.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const next = (idx + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        tabs[next].focus();
        activate(next);
      });
    });
    document.addEventListener('shopify:block:select', (e) => {
      const i = panels.indexOf(e.target);
      if (i > -1) activate(i);
    });
  };

  /* ---------- Reveal on scroll ---------- */
  const revealIn = (scope) => $$('.reveal', scope).forEach((el) => el.classList.add('is-visible'));
  const initReveal = () => {
    const els = $$('.reveal');
    if (!('IntersectionObserver' in window) || reduceMotion) return els.forEach((el) => el.classList.add('is-visible'));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const siblings = $$('.reveal', entry.target.parentElement);
        entry.target.style.transitionDelay = `${Math.min(siblings.indexOf(entry.target), 6) * 70}ms`;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
  };

  /* ---------- Cart drawer ---------- */
  const drawer = () => $('[data-cart-drawer]');
  let lastFocus = null;

  const openCart = () => {
    const d = drawer();
    if (!d) { window.location.href = L.routes.cart; return; }
    lastFocus = document.activeElement;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('no-scroll');
    setTimeout(() => $('.cart-drawer__panel', d)?.focus(), 50);
  };
  const closeCart = () => {
    const d = drawer();
    if (!d || !d.classList.contains('is-open')) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('no-scroll');
    lastFocus?.focus?.();
  };

  const renderDrawer = (html) => {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = $('.cart-drawer__panel', doc);
    const current = $('.cart-drawer__panel');
    if (fresh && current) {
      current.innerHTML = fresh.innerHTML;
      current.dataset.count = fresh.dataset.count;
    }
    const count = fresh?.dataset.count;
    if (count !== undefined) {
      $$('[data-cart-count]').forEach((b) => { b.textContent = count; b.hidden = count === '0'; });
    }
  };

  const changeLine = async (line, quantity) => {
    const panel = $('.cart-drawer__panel');
    panel?.classList.add('is-loading');
    try {
      const res = await fetch(`${L.routes.cart_change}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Math.max(0, Number(quantity)), sections: ['cart-drawer'] }),
      });
      const data = await res.json();
      renderDrawer(data.sections?.['cart-drawer']);
    } catch (err) {
      window.location.reload();
    } finally {
      panel?.classList.remove('is-loading');
    }
  };

  const showFormError = (form, message) => {
    const el = $('[data-form-error]', form);
    if (el) { el.textContent = message; el.hidden = false; } else { window.alert(message); }
  };

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form || !drawer() || !window.fetch) return;
    e.preventDefault();
    const btn = $('[type="submit"]', form);
    btn?.setAttribute('aria-busy', 'true');
    const err = $('[data-form-error]', form);
    if (err) err.hidden = true;

    const body = new FormData(form);
    body.append('sections', 'cart-drawer');
    body.append('sections_url', window.location.pathname);
    try {
      const res = await fetch(`${L.routes.cart_add}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        showFormError(form, data.description || data.message || 'Virhe');
        return;
      }
      renderDrawer(data.sections?.['cart-drawer']);
      openCart();
    } catch (error) {
      form.submit();
    } finally {
      btn?.removeAttribute('aria-busy');
    }
  });

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-cart-toggle]');
    if (toggle && drawer() && document.body.dataset.template !== 'cart') {
      e.preventDefault();
      openCart();
      return;
    }
    if (e.target.closest('[data-cart-close]')) { e.preventDefault(); closeCart(); return; }
    const line = e.target.closest('[data-line]');
    if (line) { e.preventDefault(); changeLine(line.dataset.line, line.dataset.qty); return; }

    const step = e.target.closest('[data-qty-step]');
    if (step) {
      const input = $('input', step.parentElement);
      const min = Number(input.min || 0);
      input.value = Math.max(min, (Number(input.value) || 0) + Number(step.dataset.qtyStep));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // Close mobile nav after following an in-page link
    if (e.target.closest('.mobile-nav__panel a')) $('[data-mobile-nav]')?.removeAttribute('open');
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    closeCart();
    $$('details[open]').forEach((d) => {
      if (d.matches('.mobile-nav, .header-search, .filter')) d.removeAttribute('open');
    });
  });

  // Close search / filter popovers when clicking outside
  document.addEventListener('click', (e) => {
    $$('.header-search[open], .filter[open]').forEach((d) => {
      if (!d.contains(e.target)) d.removeAttribute('open');
    });
  });

  // Focus search input when opened; set mobile panel offset
  $$('.header-search').forEach((d) => d.addEventListener('toggle', () => { if (d.open) $('input[type="search"]', d)?.focus(); }));
  $$('[data-mobile-nav]').forEach((d) => d.addEventListener('toggle', () => {
    const header = d.closest('.site-header');
    if (header) document.documentElement.style.setProperty('--mobile-nav-top', `${header.getBoundingClientRect().bottom}px`);
    document.documentElement.classList.toggle('no-scroll', d.open);
  }));

  /* ---------- Product page ---------- */
  const showMedia = (section, id) => {
    $$('[data-media-id]', section).forEach((m) => m.classList.toggle('is-active', m.dataset.mediaId === String(id)));
    $$('[data-thumb]', section).forEach((t) => t.classList.toggle('is-active', t.dataset.thumb === String(id)));
  };

  const initProduct = (section) => {
    $$('[data-thumb]', section).forEach((t) => t.addEventListener('click', () => showMedia(section, t.dataset.thumb)));

    const json = $('[data-variants]', section);
    if (!json) return;
    const variants = JSON.parse(json.textContent);
    const selects = $$('[data-option]', section);
    const idInput = $('form[data-product-form] input[name="id"]', section);
    const btn = $('[data-add-button]', section);
    const price = $('[data-price]', section);

    section.addEventListener('change', (e) => {
      if (!e.target.matches('[data-option]')) return;
      const values = selects.map((s) => s.value);
      const v = variants.find((variant) => variant.options.every((o, k) => o === values[k]));
      if (!v) {
        if (btn) { btn.disabled = true; btn.textContent = btn.dataset.unavailable; }
        return;
      }
      if (idInput) idInput.value = v.id;
      if (btn) {
        btn.disabled = !v.available;
        btn.textContent = v.available ? btn.dataset.add : btn.dataset.soldout;
      }
      if (price) {
        const sale = v.compare_at_price > v.price;
        price.classList.toggle('price--sale', sale);
        price.innerHTML = `<span class="price__current">${money(v.price)}</span>${sale ? `<s class="price__compare">${money(v.compare_at_price)}</s>` : ''}`;
      }
      if (v.featured_media) showMedia(section, v.featured_media.id);
      if (section.dataset.url) window.history.replaceState({}, '', `${section.dataset.url}?variant=${v.id}`);
    });
  };

  /* ---------- Recommendations ---------- */
  const initRecommendations = async (el) => {
    if (!el.dataset.url) return;
    try {
      const html = await (await fetch(el.dataset.url)).text();
      const fresh = $('[data-recommendations]', new DOMParser().parseFromString(html, 'text/html'));
      if (fresh && fresh.innerHTML.trim()) {
        el.innerHTML = fresh.innerHTML;
        requestAnimationFrame(() => revealIn(el));
      }
    } catch (e) { /* ignore */ }
  };

  /* ---------- Collection filters ---------- */
  const initFacets = (form) => {
    form.addEventListener('change', () => {
      const params = new URLSearchParams(new FormData(form));
      window.location.search = params.toString();
    });
  };

  /* ---------- Init ---------- */
  const init = () => {
    $$('[data-slideshow]').forEach(initSlideshow);
    $$('[data-tabs]').forEach(initTabs);
    $$('[data-product-section]').forEach(initProduct);
    $$('[data-recommendations]').forEach(initRecommendations);
    $$('[data-facets-form]').forEach(initFacets);
    initReveal();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Re-init when sections are edited in the theme editor
  document.addEventListener('shopify:section:load', (e) => {
    $$('[data-slideshow]', e.target).forEach(initSlideshow);
    $$('[data-tabs]', e.target).forEach(initTabs);
    $$('[data-product-section]', e.target).forEach(initProduct);
    revealIn(e.target);
  });
})();
